import React, { createContext, ReactNode, useState } from "react";

import { IUser } from "../lib/types";

interface IOfficeAppContext {
    selected_email: any | undefined; // Office.Mailbox["item"] when available
    user: IUser | undefined;
    tasks: any[] | undefined;
    is_loading: boolean;
    is_authenticating: boolean;
    error: string | undefined;
    current_page: Pages;
    setUser: (user: IUser) => void;
    setTasks: (tasks: any[]) => void;
    setIsLoading: (isLoading: boolean) => void;
    setError: (error: string | undefined) => void;
    setIsAuthenticating: (isAuthenticating: boolean) => void;
    setSelectedEmail: (email: any | undefined) => void;
    setCurrentPage: (page: Pages) => void;
    getEmailProcessingStatus: () => EmailProcessingStatus;
    setEmailProcessingStatus: (emailId: string, status: EmailProcessingStatusItem) => void;
}

export interface EmailProcessingStatusItem {
    status: string;
    extractionId: string;
    errorMessage?: string;
}

export interface EmailProcessingStatus {
    [key: string]: EmailProcessingStatusItem;
}

export enum Pages {
    "Dashboard",
    "EmailSelected",
}

export const EMAIL_PROCESSING_STATUS_KEY = "emailProcessingStatus";

const OfficeAppContext = createContext<IOfficeAppContext | undefined>(undefined);

export const OfficeAppProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<IUser | undefined>(undefined);
    const [tasks, setTasks] = useState<any[] | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [is_loading, setIsLoading] = useState<boolean>(false);
    const [selected_email, setSelectedEmail] = useState<any | undefined>(undefined);
    const [is_authenticating, setIsAuthenticating] = useState<boolean>(false);
    const [current_page, setCurrentPage] = useState<Pages>(Pages.Dashboard);

    const getEmailProcessingStatus = (): EmailProcessingStatus => {
        return (
            JSON.parse(localStorage.getItem(EMAIL_PROCESSING_STATUS_KEY) || '{}') ?? ({} as EmailProcessingStatus)
        );
    };

    const setEmailProcessingStatus = (emailId: string, status: EmailProcessingStatusItem) => {
        const current_status = getEmailProcessingStatus();
        localStorage.setItem(
            EMAIL_PROCESSING_STATUS_KEY,
            JSON.stringify({
                ...current_status,
                [emailId]: status,
            })
        );
    };

    return (
        <OfficeAppContext.Provider
            value={{
                current_page: current_page,
                selected_email: selected_email,
                user,
                tasks,
                is_loading: is_loading,
                is_authenticating: is_authenticating,
                error,
                setUser,
                setTasks,
                setIsLoading,
                setError,
                setIsAuthenticating,
                setSelectedEmail,
                setCurrentPage,
                getEmailProcessingStatus,
                setEmailProcessingStatus,
            }}
        >
            {children}
        </OfficeAppContext.Provider>
    );
};

export const useOfficeAppContext = (): IOfficeAppContext => {
    const context = React.useContext(OfficeAppContext);
    if (!context) {
        throw new Error("useOfficeAppContext must be used within an OfficeAppProvider");
    }
    return context;
};